// PLAY-03 多路同步回放（最多 4 路）：選攝影機與時段 → 每路一個回放 session（façade，各自重驗授權）→
// 共用一個牆上時鐘對齊（sync.ts）。某一路那個時間沒有錄影就顯示空檔、等時鐘走到它的下一段。
// 本地來源（SPEC PLAY-03「多路同步的本地來源」，app/vms#25）：雲端在範圍內有片段就播雲端；沒有就經回放隧道播 NVR / Station 硬碟或 SD 卡
// （一路一條隧道、帶通道記稽核；每台主機最多 LOCAL_PER_HOST 路；子碼流優先、10 分鐘清單接續；對齊用 localDecide）。
import { DateTimeInput } from '../../components/DateTimeInput';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import Hls from 'hls.js';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { KinioError, PlaybackTunnel, vms } from '@kinio/web-sdk';
import { eventOnlyCloud } from './cloudCams';
import { useDevices } from '../../lib/useDevices';
import { can, fullName, type VmsDevice } from '../../lib/devices';
import { errorKeys } from '../../lib/errorKeys';
import { formatDateTime, formatTime } from '../../lib/tz';
import { nearStamp } from '../../lib/timefmt';
import { cloudSpans } from '../../lib/facade';
import { fragsSuspect } from '../events/clipGuard';
import { playbackHref } from '../../lib/links';
import { useTzResolver } from '../../lib/useTz';
import { PlaybackModes } from './PlaybackPage';
import { useCloudCams } from './cloudCams';
import { LOCAL_KINDS, hostOf, localOnline, localPlanOf, useLocalCams } from './localCams';
import { localCoverageIn } from '../../lib/localCoverage';
import { localPlaylistUrl, makeTunnelLoader } from '../../lib/tunnelLoader';
import {
  LOCAL_PER_HOST,
  SYNC_MAX,
  SYNC_TICK_MS,
  advanceClock,
  alignStart,
  avgCost,
  coverRatio,
  laneSource,
  localDecide,
  localWindow,
  needsSeek,
  needsWindow,
  nextWindowAt,
  pushSeekCost,
  seekTarget,
  wallTime,
  type FragLike,
  type LaneSource,
} from './sync';
import type { SourcePlan } from './view';

const SPEEDS = [0.5, 1, 2, 4];

/** datetime-local 的值 → unix 秒（不合法 = undefined）。 */
function fromMs(v: string): number | undefined {
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

function localInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}

type TileState = 'loading' | 'ready' | 'gap' | 'end' | 'error';

interface Tile {
  cam: VmsDevice;
  state: TileState;
  err?: string;
  /** gap：下一段雲端片段從什麼時候開始（ms） */
  nextAt?: number;
  /** end / 沒有錄影：這段時間之後（往後找 LATER_HOURS 小時）下一段雲端片段的開始（ms）；null = 找過、沒有；undefined = 還沒找 */
  later?: number | null;
  /** 播放清單裡有長度不合理 / 結束在未來的雲端片段（第六輪 B6-01：畫面可能是綠色或停住） */
  suspect?: boolean;
  /** 這一路播哪個來源（雲端 / 硬碟 / SD 卡） */
  source?: LaneSource;
  /** 選來源的依據（格子上的 data-cloud-cover / data-local，現場查「為什麼這格播雲端」用） */
  why?: { cover: number; local: string };
}

/** 本地的路（經回放隧道）：目前的 10 分鐘清單、seek 花的時間、等清單 / 等 seek 中。 */
interface LocalLane {
  plan: SourcePlan;
  win: { from: number; to: number };
  costs: number[];
  seekAt?: number;
  reloading?: boolean;
}

/** 本地 HLS 的 hls.js 設定（同單路）：經隧道慢，第一個位元組等 30 秒、重試 2 次。 */
const LOCAL_HLS = {
  enableWorker: true,
  maxBufferLength: 20,
  fragLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: 30_000,
      maxLoadTimeMs: 60_000,
      timeoutRetry: { maxNumRetry: 2, retryDelayMs: 500, maxRetryDelayMs: 2000 },
      errorRetry: { maxNumRetry: 2, retryDelayMs: 500, maxRetryDelayMs: 2000 },
    },
  },
};

/** 這段時間之後沒有錄影的格子：往後找多久的下一段（第五輪 B5-06） */
const LATER_HOURS = 48;

/** 這一路是「這段時間之後沒有錄影」（播放清單是空的，或時鐘已經過了最後一段）。 */
const afterNone = (x: Tile) => x.state === 'end' || (x.state === 'error' && x.err === 'no_recording_after');

/** 量對齊誤差用（dev 實測 VMS-PLAY-03-L1 / L2）：每一路的影片元素記下這一刻的畫面時間與時鐘（ms），不觸發重畫。 */
function markWall(v: HTMLVideoElement, wall: number | null, clockMs: number) {
  v.dataset.wall = wall === null ? '' : String(Math.round(wall));
  v.dataset.clock = String(Math.round(clockMs));
}

function fragsOf(hls: Hls | undefined): FragLike[] {
  const d = hls?.levels?.[hls.currentLevel >= 0 ? hls.currentLevel : 0]?.details;
  return (d?.fragments ?? []).map((f) => ({ start: f.start, duration: f.duration, programDateTime: f.programDateTime ?? null }));
}

export function SyncPlaybackPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { tree, cameras: allCameras } = useDevices();
  // 多路回放只列有 recording.read 的（「我的設備」的 actions）
  const cameras = useMemo(() => allCameras.filter((c) => can(c, 'recording.read')), [allCameras]);
  const tzOf = useTzResolver();
  // 從別的分段來：網址 ?cam=a,b&t=<unix 秒>（重新整理也還在），沒有才看路由 state（第二輪 B2-10）
  const [sp] = useSearchParams();
  const st = location.state as { cameras?: string[]; at?: number } | null;
  const nav: { cameras?: string[]; at?: number } | null = sp.get('cam') || sp.get('t')
    ? { cameras: (sp.get('cam') ?? '').split(',').filter(Boolean), at: Number(sp.get('t')) || undefined }
    : st;
  const [picked, setPicked] = useState<string[]>(() => (nav?.cameras ?? []).slice(0, SYNC_MAX));
  const [from, setFrom] = useState(() => localInput((nav?.at ? nav.at * 1000 : Date.now() - 15 * 60_000) - 60_000));
  const [minutes, setMinutes] = useState(10);
  const [range, setRange] = useState<{ from: number; to: number } | null>(null);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [clock, setClock] = useState(0);
  /** 起點對齊的提示（「09:14 沒有錄影，從最早有錄影的 09:15:55 開始」）；載入後只對齊一次 */
  const [alignNote, setAlignNote] = useState<string | null>(null);
  const aligned = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const videos = useRef(new Map<string, HTMLVideoElement>());
  const players = useRef(new Map<string, Hls>());
  const tunnels = useRef(new Map<string, PlaybackTunnel>());
  const lanes = useRef(new Map<string, LocalLane>());
  const clockRef = useRef(0);
  const playRef = useRef(false);
  const rateRef = useRef(1);
  playRef.current = playing;
  rateRef.current = rate;
  const byKey = useMemo(() => new Map(cameras.map((c) => [c.key, c])), [cameras]);
  const cloud = useCloudCams(cameras);
  const local = useLocalCams(cameras, tree);
  /** 雲端與本地都沒有錄影（帶鎖頭）；有任何一邊 / 不知道就可選 */
  const locked = (k: string) => cloud.stateOf(k) === 'no' && local.stateOf(k) === 'no';
  // 雲端只存事件片段的攝影機（錄影政策 cloud.effective_mode = event；跟攝影機錄影與儲存分頁同一份快取）：勾選清單標出來（第五輪 B5-06）
  const withCloud = useMemo(() => cameras.filter((c) => cloud.stateOf(c.key) === 'yes').slice(0, 40), [cameras, cloud]);
  const pol = useQueries({
    queries: withCloud.map((c) => ({
      queryKey: ['rec-policy', c.homeId, c.aid],
      queryFn: () => vms.recordingPolicy(c.apiEndpoint, c.homeId, c.aid) as Promise<{ cloud?: { effective?: boolean; effective_mode?: string | null } | null }>,
      staleTime: 60_000,
      retry: false,
    })),
  });
  const eventOnly = new Set(withCloud.filter((_, i) => eventOnlyCloud(pol[i]?.data)).map((c) => c.key));
  // 從別的分段帶進來的攝影機沒有雲端錄影（🔒）：不預設勾（第六輪 B6-02；多路同步目前只能播雲端）
  const unlockedOnce = useRef(false);
  useEffect(() => {
    if (unlockedOnce.current || !cameras.length || cloud.loading || local.loading) return;
    unlockedOnce.current = true;
    setPicked((l) => {
      const keep = l.filter((k) => !locked(k));
      return keep.length === l.length ? l : keep;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloud, local.loading, cameras.length]);

  const errored = useRef(new Set<string>());
  /** 已經往後找過下一段的格子（每次載入一次） */
  const laterAsked = useRef(new Set<string>());
  // 沒變就回原陣列（同步迴圈每 0.5 秒都會呼叫，不要每次都重畫）
  const setTile = (key: string, patch: Partial<Tile>) =>
    setTiles((l) => {
      const cur = l.find((x) => x.cam.key === key);
      if (!cur || (Object.keys(patch) as (keyof Tile)[]).every((k) => cur[k] === patch[k])) return l;
      return l.map((x) => (x === cur ? { ...x, ...patch } : x));
    });
  const destroy = () => {
    for (const h of players.current.values()) h.destroy();
    players.current.clear();
    for (const tun of tunnels.current.values()) tun.close();
    tunnels.current.clear();
    lanes.current.clear();
  };
  useEffect(() => destroy, []);

  /** 本地的一路：開回放隧道（帶通道與起點記稽核）→ hls.js 經隧道要 10 分鐘清單（子碼流優先，tunnelLoader）。 */
  async function openLocal(cam: VmsDevice, plan: SourcePlan, range: { from: number; to: number }) {
    const tun = new PlaybackTunnel({
      apiEndpoint: cam.apiEndpoint,
      homeId: cam.homeId,
      deviceRef: plan.localAid,
      channel: plan.auditCh,
      from: Math.floor(range.from / 1000),
    });
    tunnels.current.set(cam.key, tun);
    try {
      await tun.open();
    } catch (e) {
      errored.current.add(cam.key);
      // 設備回的碼有對應的句子就用（MAX_SESSIONS_REACHED、CHANNEL_OFFLINE…），其他一律「回放通道失敗」，不把原始碼當句子
      const code = String((e as KinioError)?.code ?? '');
      setTile(cam.key, { state: 'error', err: code && i18n.exists(`playback.err_${code}`) ? code : 'TUNNEL_FAILED' });
      return;
    }
    const video = videos.current.get(cam.key);
    if (!video || tunnels.current.get(cam.key) !== tun) return;
    const win = localWindow(range.from, range);
    lanes.current.set(cam.key, { plan, win, costs: [], reloading: true });
    const hls = new Hls({ ...LOCAL_HLS, loader: makeTunnelLoader(() => tunnels.current.get(cam.key) ?? null) });
    players.current.set(cam.key, hls);
    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      const code = data.details === 'levelEmptyError' || data.details === 'manifestParsingError' ? 'no_recording_after' : data.details === 'manifestLoadTimeOut' ? 'TUNNEL_TIMEOUT' : 'TUNNEL_FAILED';
      setTile(cam.key, { state: 'error', err: code });
    });
    hls.on(Hls.Events.LEVEL_LOADED, () => {
      const lane = lanes.current.get(cam.key);
      if (lane) lane.reloading = false;
      if (!errored.current.has(cam.key)) setTile(cam.key, { state: 'ready' });
    });
    hls.loadSource(localPlaylistUrl(plan.localCh, win.from / 1000, win.to / 1000));
    hls.attachMedia(video);
  }

  async function load(at?: number) {
    // 格子照勾選清單的順序（第七輪 B7-06：帶入的那台排第一，格子變成 CH2、CH1、CH3）
    const order = new Map(cameras.map((c, i) => [c.key, i]));
    const cams = picked
      .map((k) => byKey.get(k))
      .filter((c): c is VmsDevice => !!c)
      .sort((a, b) => (order.get(a.key) ?? 1e9) - (order.get(b.key) ?? 1e9));
    if (!cams.length) return;
    destroy();
    errored.current.clear();
    const f = at ?? new Date(from).getTime();
    const to = f + minutes * 60_000;
    setRange({ from: f, to });
    aligned.current = false;
    setAlignNote(null);
    laterAsked.current.clear();
    setClock(f);
    clockRef.current = f;
    setPlaying(false);
    setTiles(cams.map((cam) => ({ cam, state: 'loading' })));
    const fromS = f / 1000;
    const toS = to / 1000;
    // 每一路的來源（PLAY-03「多路同步的本地來源」第 2 點）：雲端在範圍內有片段 → 雲端；沒有 → 本地（開放的種類）
    const picks = await Promise.all(
      cams.map(async (cam) => {
        const plan = localPlanOf(cam, tree);
        const online = localOnline(cam, tree);
        const [cloudCover, lstate] = await Promise.all([
          cloudSpans(cam.apiEndpoint, cam.homeId, cam.aid, fromS, toS)
            .then((sp) => coverRatio(sp, fromS, toS))
            // 沒有權益 / 權限 = 沒有雲端；其他錯誤（網路）照原本先試雲端
            .catch((e) => (e instanceof KinioError && [402, 403, 404].includes(e.status) ? 0 : 1)),
          plan && online ? localCoverageIn(cam.apiEndpoint, cam.homeId, plan.localAid, plan.localCh, fromS, toS, tzOf(cam.homeId)) : Promise.resolve(plan ? ('unknown' as const) : ('absent' as const)),
        ]);
        setTile(cam.key, { why: { cover: Math.round(cloudCover * 100) / 100, local: lstate } });
        return { cam, plan, online, src: laneSource(cloudCover, plan?.localKind ?? null, lstate, LOCAL_KINDS) };
      }),
    );
    const perHost = new Map<string, number>();
    await Promise.all(
      picks.map(async ({ cam, plan, online, src }) => {
        if (src && src !== 'cloud' && plan) {
          setTile(cam.key, { source: src });
          if (!online) {
            errored.current.add(cam.key);
            setTile(cam.key, { state: 'error', err: 'local_offline' });
            return;
          }
          const host = hostOf(cam, plan);
          const n = (perHost.get(host) ?? 0) + 1;
          perHost.set(host, n);
          if (n > LOCAL_PER_HOST) {
            errored.current.add(cam.key);
            setTile(cam.key, { state: 'error', err: 'local_host_limit' });
            return;
          }
          await openLocal(cam, plan, { from: f, to });
          return;
        }
        setTile(cam.key, { source: 'cloud' });
        try {
          const ps = await vms.playbackSession(cam.apiEndpoint, cam.homeId, cam.aid, {
            source: 'cloud',
            from: new Date(f).toISOString(),
            to: new Date(to).toISOString(),
          });
          if (ps.playback.kind !== 'hls') throw new Error('unexpected playback kind');
          const video = videos.current.get(cam.key);
          if (!video) return;
          const hls = new Hls({ enableWorker: true, maxBufferLength: 30 });
          players.current.set(cam.key, hls);
          hls.on(Hls.Events.ERROR, (_e, data) => {
            if (data.fatal)
              setTile(cam.key, {
                state: 'error',
                err: data.details === 'levelEmptyError' || data.details === 'manifestParsingError' ? 'no_recording_after' : data.details,
              });
          });
          hls.on(Hls.Events.LEVEL_LOADED, () => {
            if (errored.current.has(cam.key)) return;
            // 片段長度防呆（第六輪 R6-P0-1、B6-01）：清單裡有一段太長 / 在未來 → 格子上說明、給〔用單路看其他來源〕
            setTile(cam.key, { state: 'ready', suspect: fragsSuspect(fragsOf(hls), Date.now() / 1000) });
          });
          hls.loadSource(ps.playback.playlist_url);
          hls.attachMedia(video);
        } catch (e) {
          errored.current.add(cam.key);
          setTile(cam.key, { state: 'error', err: String((e as KinioError).code ?? (e as Error).message ?? e) });
        }
      }),
    );
  }

  // 同步迴圈：時鐘往前走、每一路對到時鐘
  useEffect(() => {
    if (!range) return;
    let last = performance.now();
    const iv = setInterval(() => {
      const nowP = performance.now();
      const dt = nowP - last;
      last = nowP;
      if (playRef.current) {
        clockRef.current = advanceClock(clockRef.current, dt, rateRef.current, range.to);
        setClock(clockRef.current);
        if (clockRef.current >= range.to) setPlaying(false);
      }
      // 起點對齊（B3-13）：還沒按播放、每一路都載到清單了，選的時間全部沒有片段 → 跳到最早有片段的時間
      if (!aligned.current && !playRef.current && players.current.size > 0) {
        const lanes = [...players.current].filter(([k]) => !errored.current.has(k)).map(([, h]) => fragsOf(h));
        if (lanes.length && lanes.every((l) => l.length > 0)) {
          aligned.current = true;
          const at = alignStart(lanes, clockRef.current);
          if (at !== null && at < range.to) {
            setAlignNote(t('sync.aligned', { at: formatTime(clockRef.current / 1000, tzRef.current, i18n.language, false), from: formatTime(at / 1000, tzRef.current, i18n.language, true) }));
            clockRef.current = at;
            setClock(at);
          }
        }
      }
      for (const [key, hls] of players.current) {
        const v = videos.current.get(key);
        if (!v || errored.current.has(key)) continue;
        const lane = lanes.current.get(key);
        if (lane) {
          syncLocal(key, hls, v, lane, range);
          continue;
        }
        const frags = fragsOf(hls);
        if (!frags.length) continue;
        const target = seekTarget(frags, clockRef.current);
        if (target.kind !== 'at') {
          if (!v.paused) v.pause();
          setTile(key, target.kind === 'gap' ? { state: 'gap', nextAt: target.nextAtMs } : { state: target.kind });
          continue;
        }
        setTile(key, { state: 'ready' });
        const wall = wallTime(frags, v.currentTime);
        markWall(v, wall, clockRef.current);
        if (needsSeek(wall, clockRef.current)) v.currentTime = target.time;
        v.playbackRate = rateRef.current;
        if (playRef.current && v.paused) void v.play().catch(() => {});
        if (!playRef.current && !v.paused) v.pause();
      }
    }, SYNC_TICK_MS);
    return () => clearInterval(iv);
  }, [range]);

  /** 本地的一路對到時鐘（PLAY-03「多路同步的本地來源」第 3、4 點）：清單快到尾端 / 時鐘拖出窗外就換下一份；緩衝中不判斷。 */
  function syncLocal(key: string, hls: Hls, v: HTMLVideoElement, lane: LocalLane, range: { from: number; to: number }) {
    const clockMs = clockRef.current;
    if (lane.reloading) return;
    if (needsWindow(lane.win, clockMs, range.to)) {
      lane.win = localWindow(nextWindowAt(lane.win, clockMs), range);
      lane.reloading = true;
      lane.seekAt = undefined;
      hls.loadSource(localPlaylistUrl(lane.plan.localCh, lane.win.from / 1000, lane.win.to / 1000));
      return;
    }
    const frags = fragsOf(hls);
    if (!frags.length) return;
    const now = performance.now();
    if (lane.seekAt !== undefined) {
      if (v.seeking || v.readyState < 3) return; // 等 seek 完（記下花了多久，下次往前加）
      lane.costs = pushSeekCost(lane.costs, now - lane.seekAt);
      lane.seekAt = undefined;
    }
    const target = seekTarget(frags, clockMs);
    if (target.kind !== 'at') {
      if (!v.paused) v.pause();
      setTile(key, target.kind === 'gap' ? { state: 'gap', nextAt: target.nextAtMs } : { state: target.kind });
      return;
    }
    setTile(key, { state: 'ready' });
    v.playbackRate = rateRef.current;
    if (v.seeking || v.readyState < 2) return; // 緩衝中不判斷
    const wall = wallTime(frags, v.currentTime);
    markWall(v, wall, clockMs);
    const d = localDecide(wall, clockMs, avgCost(lane.costs), rateRef.current);
    if (d.kind === 'seek') {
      const t2 = seekTarget(frags, d.atMs);
      v.currentTime = t2.kind === 'at' ? t2.time : target.time;
      lane.seekAt = now;
    }
    if (d.kind === 'hold' || !playRef.current) {
      if (!v.paused) v.pause();
      return;
    }
    if (v.paused) void v.play().catch(() => {});
  }

  // 這段時間之後沒有錄影的格子：往後找下一段雲端片段，寫「下一段在 HH:MM〔跳過去〕」（第五輪 B5-06）
  useEffect(() => {
    if (!range) return;
    for (const x of tiles) {
      if (!afterNone(x) || laterAsked.current.has(x.cam.key) || lanes.current.has(x.cam.key)) continue;
      laterAsked.current.add(x.cam.key);
      const fromS = Math.floor(Math.max(range.from, clockRef.current) / 1000);
      void cloudSpans(x.cam.apiEndpoint, x.cam.homeId, x.cam.aid, fromS, fromS + LATER_HOURS * 3600)
        .then((spans) => {
          const n = spans.filter((sp) => sp.end > fromS).sort((a, b) => a.start - b.start)[0];
          setTile(x.cam.key, { later: n ? Math.max(n.start, fromS) * 1000 : null });
        })
        .catch(() => setTile(x.cam.key, { later: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles, range]);

  /** 〔跳過去〕：同步回放是同一個時間——整組移到那一段開始的時間重新載入，並說明 */
  const jumpTo = (ms: number, name: string) => {
    setFrom(localInput(ms));
    void load(ms).then(() => setAlignNote(t('sync.jumped', { name, at: formatTime(ms / 1000, tzRef.current, i18n.language, true) })));
  };

  const seek = (ms: number) => {
    clockRef.current = ms;
    setClock(ms);
  };
  const tz = tiles[0] ? tzOf(tiles[0].cam.homeId) : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzRef = useRef(tz);
  tzRef.current = tz;
  const toggle = (key: string) => setPicked((l) => (l.includes(key) ? l.filter((k) => k !== key) : l.length >= SYNC_MAX ? l : [...l, key]));

  return (
    <section>
      <div className="toolbar">
        <h1 className="page-title" style={{ margin: 0 }}>
          {t('sync.title')}
        </h1>
        <span className="spacer" />
        <PlaybackModes mode="sync" cameras={picked} at={range ? Math.floor(clock / 1000) : (fromMs(from) ?? nav?.at)} />
      </div>
      <p className="muted small">{t('sync.help', { max: SYNC_MAX })}</p>
      {/* 整個範圍都沒有雲端錄影：先說清楚，不讓人選完才看到一整排空格（PLAY-03「在支援本地來源之前」） */}
      {cloud.summary === 'none' && cameras.every((c) => local.stateOf(c.key) === 'no') && (
        <p className="notice small" data-testid="sync-no-cloud">
          {t('sync.no_cloud_scope')}{' '}
          <Link to="/billing#seats">{t('playback.buy_seats')}</Link>
        </p>
      )}
      <div className="card form">
        <fieldset className="field">
          <legend>{t('sync.cameras', { n: picked.length, max: SYNC_MAX })}</legend>
          {/* 為什麼有鎖頭只在清單上方講一次（第四輪 R4-P1-8：原本每張卡都重複同一句） */}
          {cloud.summary !== 'none' && cameras.some((c) => locked(c.key)) && (
            <p className="muted small" data-testid="sync-lock-legend">
              🔒 {t('sync.no_cloud_legend')}
            </p>
          )}
          <div className="check-grid">
            {cameras.map((c) => (
              <span key={c.key} className="sync-cam">
                <label className="check">
                  <input
                    type="checkbox"
                    checked={picked.includes(c.key)}
                    disabled={!picked.includes(c.key) && picked.length >= SYNC_MAX}
                    onChange={() => toggle(c.key)}
                  />
                  {/* 名字完整顯示、放不下就換行（第六輪 B6-02：被截成「lstw-」三個通道分不出來） */}
                  <span className="sync-cam-name" data-testid="sync-cam-name">
                    {fullName(c, tree)}
                  </span>
                </label>
                {/* 這台沒有雲端錄影：照列但帶鎖頭與一句（PLAY-03「在支援本地來源之前」）；鎖頭跟名字同一行（第五輪 R5-P1-1） */}
                {locked(c.key) && (
                  <span className="muted small" data-testid="sync-cam-locked" title={t('sync.no_cloud_cam')} aria-label={t('sync.no_cloud_cam')}>
                    🔒
                  </span>
                )}
                {eventOnly.has(c.key) && (
                  <span className="badge small" data-testid="sync-cam-event-only" title={t('sync.event_only_hint')}>
                    {t('sync.event_only')}
                  </span>
                )}
              </span>
            ))}
          </div>
        </fieldset>
        <div className="toolbar">
          <label className="field">
            <span>{t('exports.from')}</span>
            <DateTimeInput value={from} onChange={setFrom} />
          </label>
          <label className="field">
            <span>{t('exports.minutes')}</span>
            <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {[5, 10, 30, 60].map((m) => (
                <option key={m} value={m}>
                  {t('exports.n_min', { n: m })}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-primary" disabled={!picked.length || !from} onClick={() => void load()}>
            {t('sync.load')}
          </button>
        </div>
      </div>
      {range && (
        <>
          <div className="toolbar sync-controls">
            <button className="btn btn-primary" onClick={() => setPlaying((p) => !p)} aria-pressed={playing} data-testid="sync-play">
              {playing ? t('sync.pause') : t('sync.play')}
            </button>
            <select className="input" value={rate} onChange={(e) => setRate(Number(e.target.value))} aria-label={t('sync.speed')}>
              {SPEEDS.map((s) => (
                <option key={s} value={s}>
                  {s}×
                </option>
              ))}
            </select>
            <input
              className="sync-scrub"
              type="range"
              min={range.from}
              max={range.to}
              step={1000}
              value={clock}
              aria-label={t('sync.position')}
              onChange={(e) => seek(Number(e.target.value))}
            />
            <span className="mono small" data-testid="sync-clock">
              {formatDateTime(clock / 1000, tz, i18n.language, true)}
            </span>
          </div>
          {alignNote && (
            <p className="pb-notice small" role="status" data-testid="sync-aligned">
              {alignNote}
            </p>
          )}
          <div className="sync-grid" data-count={tiles.length}>
            {tiles.map((x) => (
              <div
                key={x.cam.key}
                className={`tile sync-tile sync-${x.state}`}
                data-sync-tile={x.cam.key}
                data-state={x.state}
                data-cloud-cover={x.why?.cover}
                data-local={x.why?.local}
              >
                <video
                  muted
                  playsInline
                  ref={(el) => {
                    if (el) videos.current.set(x.cam.key, el);
                    else videos.current.delete(x.cam.key);
                  }}
                />
                {x.state !== 'ready' && (
                  <div className="tile-overlay" data-testid="sync-tile-msg">
                    <span>
                      {/* 同一種情況同一句：「這個時間沒有雲端錄影（下一段 16:44）」＋〔跳到 16:44〕（第七輪 B7-06） */}
                      {afterNone(x) && typeof x.later === 'number'
                        ? t('sync.gap_next', { time: nearStamp(x.later, clock, tz) })
                        : afterNone(x)
                        ? t(x.source && x.source !== 'cloud' ? 'sync.end_local' : 'sync.end')
                        : x.state === 'error'
                          ? t(errorKeys('playback', x.err ?? ''), { defaultValue: x.err, name: fullName(x.cam, tree), n: LOCAL_PER_HOST })
                          : x.state === 'loading'
                            ? t('common.loading')
                            : x.nextAt
                              ? t(x.source && x.source !== 'cloud' ? 'sync.gap_next_local' : 'sync.gap_next', { time: nearStamp(x.nextAt, clock, tz) })
                              : t(x.source && x.source !== 'cloud' ? 'sync.gap_local' : 'sync.gap')}
                    </span>
                    {/* 空檔：下一段還在這次的時間範圍裡 → 時鐘跳過去；之後都沒有 → 往後找下一段，整組移過去（第五輪 B5-06） */}
                    {x.state === 'gap' && x.nextAt && (
                      <button className="btn btn-sm" onClick={() => seek(x.nextAt!)} data-testid="sync-skip">
                        {t('sync.skip_to', { time: nearStamp(x.nextAt, clock, tz) })}
                      </button>
                    )}
                    {afterNone(x) && typeof x.later === 'number' && (
                      <button className="btn btn-sm" onClick={() => jumpTo(x.later!, fullName(x.cam, tree))} title={t('sync.jump_all')} data-testid="sync-jump">
                        {t('sync.skip_to', { time: nearStamp(x.later, clock, tz) })}
                      </button>
                    )}
                    {afterNone(x) && x.later === null && <span className="small">{t('sync.none_later', { h: LATER_HOURS })}</span>}
                  </div>
                )}
                {x.suspect && (
                  <div className="sync-suspect small" role="status" data-testid="sync-clip-suspect">
                    {t('sync.clip_suspect')}{' '}
                    <Link to={playbackHref(x.cam.key, Math.floor(clock / 1000))}>{t('sync.open_single')}</Link>
                  </div>
                )}
                <div className="tile-bar">
                  <span className={`dot ${x.cam.presence}`} />
                  <span className="tile-name">{fullName(x.cam, tree)}</span>
                  {x.source && (
                    <span className="badge small" data-testid="sync-tile-source" data-source={x.source}>
                      {t(`sync.src_${x.source}`)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
